/**
 * NUROモバイル向けのAPIエンドポイント・レスポンスコード・画面パスなどの定数定義。
 * 光向けとモバイル向けでAPI/BFFや画面構成が異なるため、モバイル固有の値をここに集約している。
 */
import type { CodeAndName } from '@/utils/constants';

export namespace ApiUrlMobile {
  /** NUROモバイルBFFのベースURL */
  export const DOMAIN = process.env.NEXT_PUBLIC_BFF_MOBILE_URL;
  /** 参照系API */
  export namespace Read {
    /** 契約料金プラン情報取得 */
    export const PRICE_PLAN_EXCHANGE =
      '/api/read/v1/services/:service_id/contract/price-plan-exchange';
    /** 通話料・SMS通信明細確認 */
    export const CALL_SMS = '/api/read/v1/services/:service_id/billing/call-sms';
    /** 機器情報 */
    export const DEVICE_ENTRY = '/api/read/v1/services/:service_id/contract/device-entry';
    /** 契約端末配送情報取得 */
    export const DEVICE_DELIVERY = '/api/read/v1/services/:service_id/contract/device-delivery';
    /** GigaPlus受け取り */
    export const GIGA_PLUS = '/api/read/v1/services/:service_id/contract/giga-plus';
    /** 申込情報取得 */
    export const ORDER_APPLICATION = '/api/read/v1/services/:service_id/contract/order-application';
    /** 利用者情報取得 */
    export const USER_INFO = '/api/read/v1/services/:service_id/contract/user-info';

    /** 機器情報取得 */
    export const DEVICE = '/api/read/v1/services/:service_id/contract/device-info';
    /** 契約料金プラン情報取得 */
    export const PRICE_PLAN = '/api/read/v1/services/:service_id/contract/price-plan';
    /** オプション契約情報取得 */
    export const OPTIONS = '/api/read/v1/services/:service_id/contract/option';
    /** パケットギフト発行結果取得 */
    export const PACKET_GIFT = '/api/read/v1/services/:service_id/packet-gift';
    /** パケットギフト受取結果取得 */
    export const PACKET_GIFT_REQUEST = '/api/read/v1/services/:service_id/packet-gift-request';
    /** パケットギフトコード発行結果取得 */
    export const PACKET_GIFT_CODE = '/api/read/v1/services/:service_id/packet-gift-code';
    /** MNP転入受付情報取得 */
    export const MNP_TRANSFER_IN = '/api/read/v1/services/:service_id/contract/mnp-transfer-in';
    /** データ通信利用状況取得 */
    export const USAGE_STATUS = '/api/read/v1/services/:service_id/contract/data-traffic-usage';
    /** データ通信利用状況詳細取得 */
    export const USAGE_STATUS_DETAIL =
      '/api/read/v1/services/:service_id/contract/data-traffic-usage-detail';
    /** 通信最適化適用設定取得 */
    export const TRAFFIC_CONTROL = '/api/read/v1/services/:service_id/contract/traffic-control';
    /** チャージ情報取得 */
    export const CHARGE = '/api/read/v1/services/:service_id/charge';
    /** バリューデータフリー契約情報取得 */
    export const VALUE_DATA_FREE = '/api/read/v1/services/:service_id/contract/value-data-free';
    /** プレミアム帯域契約情報取得 */
    export const PREMIUM_BAND = '/api/read/v1/services/:service_id/contract/premium-band';
    /** 5Gオプション情報取得 */
    export const FIFTH_GENERATION = '/api/read/v1/services/:service_id/contract/fifth-generation';
    /** ウイルスバスターモバイルオ契約情報取得 */
    export const VIRUS_BUSTER_MOBILE =
      '/api/read/v1/services/:service_id/contract/virusbustermobile';
    /** キャンペーン情報取得 mobile BFF */
    export const CAMPAIGN = '/api/read/v1/services/:service_id/campaign';
    /** 音声通話サービス契約情報取得 */
    export const CALL_SERVICE = '/api/read/v1/services/:service_id/contract/call-service';
    /** 契約書面情報取得 */
    export const DOCUMENT = '/api/read/v1/services/:service_id/contract/document';
    /** 端末補償契約情報取得 */
    export const DEVICE_COMPENSATION =
      '/api/read/v1/services/:service_id/contract/device-compensation';
    /** データ前借情報取得 */
    export const DATA_ADVANCE = '/api/read/v1/services/:service_id/data-advance';
    /** キャッシュバック受取口座情報取得 */
    export const CASHBACK_INFO =
      '/api/read/v1/services/:service_id/campaign/cashback/register-account';
    /** 変更キャンセル情報取得（料金プラン契約情報） */
    export const CANCEL_EXCHANGE_PRICE_PLAN =
      '/api/read/v1/services/:service_id/contract/cancel-exchange-price-plan';
    /** 変更キャンセル情報取得（音声通話定額情報） */
    export const CANCEL_EXCHANGE_VOICE_CALL_FIXED_OPTION =
      '/api/read/v1/services/:service_id/contract/cancel-exchange-voice-call-fixed-option';

    /** 解約受付情報取得 */
    export const QUIT_INFO = '/api/read/v1/services/:service_id/contract/quit-info';
    /** eSIM契約情報取得 */
    export const ESIM = '/api/read/v1/services/:service_id/contract/esim';
    /** 返還請求明細確認 */
    export const REFUND_BILLING = '/api/read/v1/services/:service_id/billing/refund/billing';
    /** 返還請求書ダウンロード */
    export const REFUND_INVOICE_DOWNLOAD =
      '/api/read/v1/services/:service_id/billing/refund/invoice-download';
    /** 設定情報可否情報取得 */
    export const SETTINGS_RESTRICTION = '/api/read/v1/services/:service_id/settings-restriction';
    /** MNPワンストップ解約受付情報取得 */
    export const MNP_ONESTOP_QUIT_INFO =
      '/api/read/v1/services/:service_id/contract/mnp-onestop-quit-info';
    /** 連携情報取得 */
    export const MNP_ONESTOP_ALIGN_INFO =
      '/api/read/v1/services/:service_id/contract/mnp-onestop-align-info';
    /** 利用停止・再開情報取得 */
    export const STOP_RESTART = '/api/read/v1/services/:service_id/contract/stop-restart-info';
    /** レンタルWi-Fi集荷先住所取得 */
    export const RENTAL_WIFI_SEND_BACK_INFO =
      '/api/read/v1/services/:service_id/contract/rental-wifi-send-back-info';
  }
  export namespace Write {
    /** 機器情報登録 */
    export const DEVICE_ENTRY = '/api/write/v1/services/:service_id/contract/device-entry';
    /** 通信最適化適用設定取得 */
    /** GigaPlus受け取り */
    export const GIGA_PLUS = '/api/write/v1/services/:service_id/contract/giga-plus';
    /** 通信最適化適用設定 */
    export const TRAFFIC_CONTROL = '/api/write/v1/services/:service_id/contract/traffic-control';
    /** チャージ情報追加 */
    export const CHARGE = '/api/write/v1/services/:service_id/charge';
    /** バリューデータフリー契約情報更新 */
    export const VALUE_DATA_FREE = '/api/write/v1/services/:service_id/contract/value-data-free';
    /** プレミアム帯域契約情報更新 */
    export const PREMIUM_BAND = '/api/write/v1/services/:service_id/contract/premium-band';
    /** 5Gオプション情報更新 */
    export const FIFTH_GENERATION = '/api/write/v1/services/:service_id/contract/fifth-generation';
    /** プラン変更情報更新 */
    export const PRICE_PLAN_EXCHANGE =
      '/api/write/v1/services/:service_id/contract/price-plan-exchange';

    /** 端末補償契約情報更新 */
    export const DEVICE_COMPENSATION =
      '/api/write/v1/services/:service_id/contract/device-compensation';
    /** 音声通話サービス契約情報更新 */
    export const CALL_SERVICE = '/api/write/v1/services/:service_id/contract/call-service';
    /** ウイルスバスターモバイルオプション情報変更 */
    export const VIRUS_BUSTER_MOBILE =
      '/api/write/v1/services/:service_id/contract/virusbustermobile';
    /** 料金プラン契約オーダ情報キャンセル登録 */
    export const CANCEL_EXCHANGE_PRICE_PLAN =
      '/api/write/v1/services/:service_id/contract/cancel-exchange-price-plan';
    /** データ前借情報更新 */
    export const DATA_ADVANCE = '/api/write/v1/services/:service_id/data-advance';
    /** キャッシュバック受取口座情報登録 */
    export const CASHBACK_REGISTER =
      '/api/write/v1/services/:service_id/campaign/cashback/register-account';
    /** 解約受付情報登録 */
    export const QUIT_INFO = '/api/write/v1/services/:service_id/contract/quit-info';
    /** eSIM契約情報更新 */
    export const ESIM = '/api/write/v1/services/:service_id/contract/esim';
    /** パケットギフトコード発行 */
    export const PACKET_GIFT_CODE = '/api/write/v1/services/:service_id/packet-gift-code';
    /** パケットギフト受取 */
    export const PACKET_GIFT_REQUEST = '/api/write/v1/services/:service_id/packet-gift-request';
    /** MNP転入受付情報変更 */
    export const MNP_TRANSFER_IN = '/api/write/v1/services/:service_id/contract/mnp-transfer-in';
    /** 利用停止・再開情報取得 */
    export const STOP_RESTART = '/api/write/v1/services/:service_id/contract/stop-restart-info';
    /** フィルタリングサービス有効化申告 */
    export const DECLARE_FILTERING_SERVICE =
      '/api/write/v1/services/:service_id/contract/declare-filtering-service';
    /** 利用者情報変更 */
    export const USER_CHANGE = '/api/write/v1/services/:service_id/contract/user-change';
  }
}

export namespace ConstantsMobile {
  /** モバイルBFFが返却する共通レスポンスコード群 */
  export namespace ResponseCode {
    /** 正常 */
    export const NORMAL = 'normal';
    /** 対象情報なし */
    export const NOT_FOUND = 'not_found';
    export namespace PacketGift {
      /** 未契約エラー */
      export const PACKET_GIFT_NONE_CONTRACT = 'packet_gift_none_contract';
      /** パケットギフト発行済みエラー */
      export const PACKET_GIFT_ALREADY = 'packet_gift_already';
      /** SIM電話番号なしエラー */
      export const PACKET_GIFT_NONE_SIM_TEL = 'packet_gift_none_sim_tel';
      /** 月末受付不可エラー */
      export const PACKET_GIFT_CODE_MONTH_END = 'packet_gift_code_month_end';
      /** ギフト容量不足エラー */
      export const PACKET_GIFT_CAPACITY_SHORTAGE = 'capacity_shortage';
      /** ギフト可能容量超過エラー  */
      export const PACKET_GIFT_CODE_CAPACITY_OVER = 'packet_gift_code_capacity_over';
      /** 月末発行不可エラー */
      export const PACKET_GIFT_CODE_INVALID_DATE_MONTH_END =
        'packet_gift_code_invalid_date_month_end';

      /** 顧客情報取得不可or未契約エラー */
      export const PACKET_GIFT_CODE_NONE_CONTRACT_IN_POST =
        'packet_gift_code_none_contract_in_post';
      /** SIM電話番号取得不可エラー  */
      export const PACKET_GIFT_CODE_NOT_AVAILABLE_SIM_TEL =
        'packet_gift_code_not_available_sim_tel';
      /** 料金プラン契約取得不可エラー  */
      export const PACKET_GIFT_CODE_NONE_PRICE_PLAN = 'packet_gift_code_none_price_plan';
      /** パケットギフトコード発行時、情報取得不可エラー  */
      export const PACKET_GIFT_CODE_CANT_GET_IN_CODE = 'packet_gift_code_cant_get_in_code';
      /** NEOプランLite未契約エラー  */
      export const PACKET_GIFT_CODE_NOT_NEO_PLAN = 'packet_gift_code_not_neo_plan';

      /** パケットギフト受取時、情報取得不可エラー  */
      export const PACKET_GIFT_CODE_CANT_GET_IN_RECEIPT = 'cant_get_in_receipt';
      /** パケットギフト受取時、期限超過エラー */
      export const PACKET_GIFT_REQUEST_EXPIRED = 'packet_gift_request_expired';
    }

    /** リクエストエラー */
    export const BAD_REQUEST = 'bad_request';
    /** MNP予約番号有効期限エラー */
    export const MNP_TRANSFER_IN_RESERVE_NUMBER_EXPIRED = 'mnp_transfer_in_reserve_number_expired';
    /** MNP切替登録受付可能時間帯無しエラー */
    export const MNP_TRANSFER_IN_AVAILABLE_TIME_NONE = 'mnp_transfer_in_available_time_none';
    /** MNP切替登録受付時間帯予約済み時間帯エラー */
    export const MNP_TRANSFER_IN_PUT_RESERVE_EXIST = 'mnp_transfer_in_put_reserve_exist';

    /** 利用停止・再開情報変更受付エラー(対象のSIMが1つではない)  */
    export const SIM_STOP_NOT_ONE_TARGET = 'not_one_target';

    export namespace Cashback {
      export const REGISTER_ACCOUNT_CASHBACK_PROCEDURE_COMPLETE =
        'register_account_cashback_procedure_complete';
    }
    export namespace CancelPlan {
      export const CANCEL_ERROR = 'cancel_error';
    }

    /** 解約操作中の日付変更エラー */
    export namespace Quit {
      export const DATA_CHANGED = 'date_changed';
    }

    export namespace GigaPlus {
      /** GigaPlus付与情報なし */
      export const GIGA_PLUS_NOT_EXIST = 'giga_plus_not_exist';
      /* GigaPlus受取手続完了済み */
      export const GIGA_PLUS_RECEIPT_COMPLETE = 'giga_plus_receipt_complete';
      /* GigaPlus受取期限超過済み */
      export const GIGA_PLUS_RECEIPT_EXPIRED = 'giga_plus_receipt_expired';
    }

    export namespace Onestop {
      /** 予約番号期限エラー */
      export const ONESTOP_EXPIRED = 'onestop_expired';
    }
  }

  // プランコード
  export namespace PlanCode {
    /** かけ放題プランコード */
    export const UNLIMITED_PLAN_CODE = [
      'VB1982',
      'VB1992',
      'VB2002',
      'VB2012',
      'VB2202',
      'VB2212',
      'VB2222',
      'VB2232',
      'VB2242',
      'VB2252',
      'VB2262',
      'VB2272',
    ];
  }

  export namespace MNP {
    export const TIME_LABELS = [
      { timeDisplay: '09:00から11:00', timeKey: '09' },
      { timeDisplay: '11:00から13:00', timeKey: '11' },
      { timeDisplay: '13:00から15:00', timeKey: '13' },
      { timeDisplay: '15:00から17:00', timeKey: '15' },
      { timeDisplay: '17:00から19:00', timeKey: '17' },
      { timeDisplay: '19:00から20:30', timeKey: '19' },
    ];
    export const SELECTOR_TRUE = 'この日にする';
    export const SELECTOR_FALSE = '選択できません';
    export const DISPLAY_TABLE_COLUMN_NUM = 4;

    export namespace ErrorMessage {
      export const CANT_DISPLAY = 'ページを表示できません。トップページより再操作してください';
      export const RESERVE_NUMBER_EXPIRED = 'ご登録いただいているMNP有効期限が過ぎております。';
      export const AVAILABLE_TIME_NONE = 'MNPの開通予約を受け付け可能な日時がありません。';
      // TODO: 文言治す
      export const RESERVE_EXIST = 'お客様が予約した開通予約時間は既に埋まりました。';
    }
    export namespace QuitStatus {
      export const WAIT_MNP_PORT_OUT_NUMBER = 'wait_mnp_port_out_number';
      export const WAIT_MNP_PORT_OUT = 'wait_mnp_port_out';
    }
  }
  export namespace DataAdvance {
    export namespace ErrorMessage {
      export const CAPACITY_SHORTAGE = '前借できる容量を超えています。';
      export const EXPIRED_DATE = 'お申込期間外のため、お手続きできません。';
      export const CANT_DISPLAY = 'データ前借がお申し込みいただけない状態です。';
    }
  }

  export namespace DeviceEntry {
    export namespace Messages {
      export const NO_DEVICE_SELECT = '機種を選択してください。';
      export const NO_COLOR_SELECT = '機種の色を選択してください';
      export const NO_STATE_SELECT = 'エラーが発生しました。初めからやり直してください。';
      export const NO_DEVICE =
        '現在、取り扱い機種がございません。今後ラインナップを充実してまいりますので、しばらくお待ちください。';
      export const NO_HOPE_DATE = '配送希望時間帯を指定する場合、配送希望日を指定してください。';
      export const NO_HOPE_DATE_TIME =
        '配送希望日を指定する場合、「指定なし」以外を指定してください。';
    }
  }

  export namespace Cashback {
    export const PAYMENT_PROCEDURE_COMPLETED = 'すでにキャッシュバックの入金手続が行われています。';
  }

  export namespace Quit {
    export namespace ContractType {
      export const MOBILE_DATA = 'mobile_data';
      export const MOBILE_SMS = 'mobile_sms';
      export const MOBILE_VOICE = 'mobile_voice';
    }
    export namespace ErrorMessage {
      export const DATA_CHANGED = '手続き中に日付が変わったため、解約予定日を再取得しました。';
    }
  }

  export namespace Option {
    export namespace Message {
      export const CAN_NOT_DISPLAY = '本オプションの申込みはできません。';
    }

    export namespace UseState {
      export const NONE = '未申込';
      export const USING = '利用中';
    }

    export namespace OperationType {
      export const ADD = 'add';
      export const REMOVE = 'remove';
      export const UPDATE = 'update';
    }

    export namespace MonthlyFee {
      export const VALUE_DATA_FREE = 0;
      export const NETWORK_FIFTH_GENERATION = 0;
      export const VIRUS_BUSTER_MOBILE = 275;
      export const DEVICE_COMPENSATION = 550;
    }

    export namespace PhoneProtection {
      export namespace Operation {
        export const QUIT = 'quit';
        export const IMEI = 'imei';
      }
      export namespace Imei {
        export namespace ErrorCode {
          /** 端末補償、現在利用中の端末補償のオプションが端末補償型変更不可エラー  */
          export const PHONE_PROTECTION_CANT_UPDATE_IMEI = 'phone_protection_cant_update_imei';
          /** 端末補償、SIM機器追加手続き中変更不可エラー  */
          export const PHONE_PROTECTION_CANT_UPDATE_IMEI_DEVICE_ADDING =
            'phone_protection_cant_update_imei_device_adding';
          /** 端末補償、サービス利用開始の翌月末まで変更不可エラー  */
          export const PHONE_PROTECTION_CANT_UPDATE_IMEI_BEFORE_NEXT_MONTH_END =
            'phone_protection_cant_update_imei_before_next_month_end';
        }
        export namespace ErrorMessage {
          export const NO_CHANGE = '変更後のIMEIを入力してください。';
          export const NOT_INPUT = 'IMEI(端末製造番号)の欄が未入力です。';
          export const NOT_LENGTH = 'IMEI(端末製造番号)は15桁で入力してください。';
          export const NOT_HALFNUMBER =
            'IMEI(端末製造番号)の欄の入力に誤りがあります。半角数字で入力してください。';
          export const CAN_NOT_UPDATE_IMEI_DEVICE_ADDING =
            'SIMまたは機器追加手続き中はIMEIを変更できません。';
          export const CAN_NOT_UPDATE_BEFORE_NEXT_MONTH_END =
            'サービス利用開始の翌月末まではIMEIを変更できません。';
          export const CAN_NOT_UPDATE_IMEI = 'お客様のご契約内容ではIMEIを変更できません。';
        }
      }
    }

    export namespace OptionKeys {
      export const VIRUS_BUSTER_MOBILE = 'VirusBusterMobile';
      export const PREMIUM_BAND = 'PremiumBand';
      export const VALUE_DATA_FREE = 'ValueDataFree';
      export const DEVICE_PURCHASE = 'DevicePurchase';
      export const DEVICE_COMPENSATION = 'DeviceCompensation';
      export const NETWORK_FIFTH_GENERATION = 'NetworkFifthGeneration';
      export const I_FILTER = 'IFilter';
    }
  }

  export namespace Esim {
    export namespace Fee {
      export const EXCHANGE = 440;
      export const ISSUE = 440;
      export const REISSUE = 440;
    }
  }

  export namespace CallSms {
    export namespace Message {
      export const CAN_NOT_DISPLAY = '表示可能な情報が存在しません。';
    }
  }

  export namespace PacketGift {
    export namespace ErrorMessage {
      export const CANT_PACKET_GIFT = '本日はパケットギフトのお申込みが受付できません';
      export const INVALID_DATE_MONTH_END =
        '月末日のためギフトコードの発行は受付できません\n発行可能期間は、毎月１日～末日の前日までとなります';
      export const CAPACITY_OVER = 'ギフトとして指定できる容量を超えています';
      export const NOT_AVAILABLE_TEL =
        '入力された電話番号はパケットギフトをご利用いただけない状態です。';
      export const CANT_PLAN =
        '入力された電話番号は対象外のプランため、パケットギフトはご利用いただけません。';
      export const CODE_DISABLE =
        '入力されたギフトコードもしくは認証コードは有効ではありません。入力内容を再度ご確認ください。';
      export const CODE_EXPIRED = '入力されたギフトコードは有効期限切れです。';
      export const TIME_OUT =
        '一時的に混雑している可能性があります。時間を置いてから、パケットギフトページでギフトコードをご確認ください。';
    }
  }

  export namespace Charge {
    export namespace Message {
      export const NOT_CHARGE_OPTION = 'オプションをご選択してください';
    }

    export namespace ChargeCategory {
      export const NORMAL = 'normal';
      export const NEO = 'neo';
    }
  }

  export namespace PlanChange {
    export namespace Condition {
      export const NEO_PLANS = ['neo', 'neo_lite', 'neo_w'];
      export const TWENTY_GB_SELLER_PLANS = ['neo', 'neo_w'];
      export const VALUE_PLANS = [
        `value_sml_data_sms`,
        'value_sml_voice',
        'value_sml_voice_limited',
      ];
      export const NOT_ADVANCE_PLANS = ['time', 'sml'];
      export const CALLING_FIVE_UNLIMITED_PLAN = {
        plan_code: 'VO4012',
        plan_type: 'calling_five_unlimited',
      };
      export const CALLING_TEN_UNLIMITED_PLAN = {
        plan_code: 'VO3052',
        plan_type: 'calling_ten_unlimited',
      };
    }
    export namespace PLAN_TYPE {
      // かけ放題プラン（5分/10分）
      export const TEL_FIXED_UP_TO_MINUTES_SELLER = 'TelFixedUpToMinutesSeller';
      // 完全かけ放題プラン
      export const TEL_FIXED_ALL_MINUTES_SELLER = 'TelFixedAllMinutesSeller';
      // NEOプラン（20GB）
      export const TWENTY_GB_SELLER = 'TwentyGbSeller';
    }

    export namespace ErrorMessage {
      export const SELECT_PLAN = 'プラン';
      export const SELECT_PREMIUM = 'プレミアムオプション';
      export const SELECT_CALL_FLAT = '通話定額オプション';
      export const SELECT_ALL_PLAN = 'プランを変更してください';
    }
  }
  export namespace Reimbursement {
    export namespace Message {
      export const NOT_CAN_DISPLAY = '対象の返還請求明細がありません。';
    }
  }

  export namespace Plan {
    export namespace CALL_FLATS {
      export const CALL_FLAT_RATE = 0;
      export const UNLIMITED = 1430;
      export const THREE_MINUTES = 660;
      export const FIVE_MINUTES = 490;
      export const TEN_MINUTES = 880;
    }
    export const CALL_FLAT_EXPIRED = '超過料金は11円/30秒';
    export const CALL_PRICE = '通話料金は11円/30秒';
  }

  export namespace GigaPlus {
    export namespace ErrorMessage {
      export const CANT_DISPLAY = 'ページを表示できません。トップページより再操作してください。';
      export const RECEIPT_COMPLETE = 'お受取り手続きは完了しております。';
      export const RESERVE_NUMBER_EXPIRED = 'お受取り期限を超過しています。';
    }
  }

  export namespace Payment {
    export namespace ErrorMessage {
      export const AUTHORITY_NG =
        'ご登録のクレジットカードの認証ができませんでした。\n登録内容を再度ご確認ください。\nご不明な点については、カード発行会社にお問い合わせください。';
      export const MAINTENANCE =
        '現在、クレジットカード認証システムのメンテナンス中です。\nしばらく経ってから操作してください。';
    }
  }

  export namespace OneStop {
    export namespace ErrorMessage {
      export const QUIT_MNP =
        '現在以下のいずれかの場合に該当するためワンストップ方式でのMNPを行うことができません。\n・サービス利用停止中の場合\n・既にMNP転出解約済みであるが、MNP予約番号未発行な場合';
      export const LOGIN =
        '入力いただいたアカウントはNUROモバイルの契約がない、またはNURO 光とNUROモバイルが別契約となっている場合。';
      export const MNP_TRANSFER =
        'すでにお客様自身で予約番号を発行されており、有効期限が残り10日未満の場合。';
    }
  }

  // フィルタリングサービス解約理由
  export namespace FilteringQuitReason {
    export const ALTERNATIVE: CodeAndName = {
      code: 'R01',
      name: '別のフィルタリングサービスを利用するため',
    };
    export const SELF_MANAGEMENT: CodeAndName = {
      code: 'R02',
      name: '契約者にて未成年者の利用状況を適切に把握し、管理するため',
    };
    export const DISABILITY: CodeAndName = {
      code: 'R03',
      name: '未成年者の障がい、病気により、フィルタリングサービスを利用することで、生活に支障が出るため',
    };
    export const WORK: CodeAndName = {
      code: 'R04',
      name: '未成年者が仕事をしており、フィルタリングサービスを利用することで、仕事に支障が出るため',
    };

    export const ALL: CodeAndName[] = [ALTERNATIVE, SELF_MANAGEMENT, DISABILITY, WORK];
  }
}

export namespace PagePathMobile {
  export const top = '/mobile/top/';
  export const contract = '/mobile/contract/';
  export const option = '/mobile/option/';
  export const detail = '/mobile/contract/detail/';
  export const packetGift = '/mobile/top/packet_gift/';
  export const packetGiftReceipt = '/mobile/top/packet_gift/receipt';
  export const reimbursement = '/mobile/reimbursement/';
  export const mnpOnestopLogin = '/mobile/onestop/login';
  export const mnpOnestopError = '/mobile/onestop/error/';
  export const login = '/login/';
}

/** 中古端末プランコード（端末補償オプション対象外） */
export const USED_DEVICE_PLAN_CODES = ['VI0435', 'VI0436', 'VI0437', 'VI0438', 'VI0439', 'VI0440'];
